import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { isInappEnv } from '@pubstack/common/src/site'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Colors } from '~/assets/style/colors'
import { ActionButtons, ActionsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { Tooltip } from '~/components/Tooltip'
import Table, { TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow, { ROW_CELL_CLASSES } from '~/components/table/TableRow'
import { ConnectToPubstack as ConnectToPubstackInstructions } from '~/feature-components/connect/ConnectToPubstack'
import { CreateAdUnitsFlyout } from '~/modules/pbs/adunits/CreateAdUnitsFlyout'
import { ViewAdUnitFlyout } from '~/modules/pbs/adunits/ViewAdUnitFlyout'
import { PbsSitePageContext } from '../useSitePageContext'

const TRACKING_LABEL = 'pbs-connect-to-pubstack'
const FLYOUT_WIDTH = '600px'
const COLUMNS: TableColumns<PbsAdUnit> = [
  {
    name: 'Server-side ad units',
    isSortable: false,
  },
  {
    name: 'Actions',
    isSortable: false,
  },
]

/** The card every section of this page sits in. */
const CARD = 'flex flex-col gap-5 bg-white p-5'

export const ConnectToPubstackContent: FunctionComponent<PbsSitePageContext> = ({ adUnits, site, isLoading, scopeId }) => {
  const flyout = useGlobalFlyout()
  const navigate = useNavigate()
  const isInapp = isInappEnv(site.env)

  const handleViewClick = (event: React.MouseEvent<HTMLElement>, adUnit: PbsAdUnit) => {
    event.preventDefault()
    event.stopPropagation()
    const siteId = site.id
    flyout.open(ViewAdUnitFlyout, { color: Colors.Ming, width: FLYOUT_WIDTH, closeTrackingLabel: `${TRACKING_LABEL}-adUnit-view-close` }, { adUnit, siteId })
  }

  const handleNewClick = () =>
    flyout.open(CreateAdUnitsFlyout, { color: Colors.Kaiminus, width: FLYOUT_WIDTH, closeTrackingLabel: `${TRACKING_LABEL}-adUnit-create-close` }, { scopeId, onClose: () => flyout.close() })

  return (
    <div className="flex flex-col gap-[22px]">
      <div className={CARD}>
        <ConnectToPubstackInstructions site={site} trackingPrefix={TRACKING_LABEL} onActivate={() => navigate(`/monetization/node/sites/${site.id}/activation`)} />
      </div>

      <div className={CARD}>
        <p className="m-0 font-roboto text-base leading-[1.625rem] text-jet">
          Add server-side ad units {isInapp && 'in the Prebid SDK configuration'}
          {/* Not "on pbjs.config", which is no Prebid API: the bid below goes in each ad unit's own `bids`. */}
          {site.env === 'web' && 'to your Prebid ad units'}
          {site.env === 'amp' && 'on AMP pages'} to connect them to Pubstack.
        </p>
        {isLoading || adUnits.length > 0 ? (
          <Table columns={COLUMNS} isLoading={isLoading}>
            {adUnits.map((adUnit) => (
              <TableRow
                key={adUnit.id}
                // A disabled ad unit is still listed — it is part of the inventory being connected — but
                // reads back dimmed. On the cells rather than the row: the row carries its own colour.
                className={cn(ROW_CELL_CLASSES, !adUnit.enabled && '[&_td]:text-black/40')}
                clickable={{
                  onClick: (e) => handleViewClick(e, adUnit),
                  trackingLabel: `${TRACKING_LABEL}-adUnit-view`,
                }}
              >
                <TableCell>{adUnit.name}</TableCell>
                <ActionsTableCell>
                  <ActionButtons>
                    <Tooltip title="View">
                      <Button iconName="view" variant="tertiary" title="View" onClick={(e) => handleViewClick(e, adUnit)} trackingLabel={`${TRACKING_LABEL}-adUnit-view`} />
                    </Tooltip>
                  </ActionButtons>
                </ActionsTableCell>
              </TableRow>
            ))}
          </Table>
        ) : (
          <IllustrationMessage iconName="night_sky" iconSize="190px" title="There are no server-side ad units in your inventory." message="">
            <Button iconName="add_adUnit" variant="primary" onClick={handleNewClick} trackingLabel={`${TRACKING_LABEL}-adUnit-new`}>
              Add ad units
            </Button>
          </IllustrationMessage>
        )}
      </div>
    </div>
  )
}

export const ConnectToPubstack: FunctionComponent = () => {
  const context = useOutletContext<PbsSitePageContext>()

  return <ConnectToPubstackContent {...context} />
}
